import { TypeKeys, generateActionId } from './actionsUtils.js';
import { convertToCommonErrors } from '../../common/utils/errorUtils.js';
import type { CommonError } from '../../common/types/errors.js';
import type { CrudAction, ErrorAction } from './actionsUtils.js';
import type { GenericError, ValidateSubscriptionPairingRequest } from '../../generated/api/models.js';

export interface ValidateSubscriptionPairingPayload extends ValidateSubscriptionPairingRequest {
  redirectPath: string;
}

export type ResendEmailVerificationLinkAction = CrudAction<TypeKeys.RESEND_EMAIL_VERIFICATION_LINK>;

export interface ResendEmailVerificationLinkFulfilledAction {
  type: TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FULFILLED;
}

export type ResendEmailVerificationLinkFailedAction = ErrorAction<TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FAILED>;

export const resendEmailVerificationLink = (): ResendEmailVerificationLinkAction => ({
  id: generateActionId(),
  type: TypeKeys.RESEND_EMAIL_VERIFICATION_LINK,
});

export const resendEmailVerificationLinkFulfilled = (): ResendEmailVerificationLinkFulfilledAction => ({
  type: TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FULFILLED,
});

export const resendEmailVerificationLinkFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): ResendEmailVerificationLinkFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.RESEND_EMAIL_VERIFICATION_LINK_FAILED,
});

// Onboard employee actions
export interface EmployeeOnBoardingPayload {
  recipientEmails: string[];
  subscriptionId?: string;
}

export interface InitiateEmployeeOnBoardingAction extends CrudAction<TypeKeys.INITIATE_EMPLOYEE_ONBOARDING> {
  payload: EmployeeOnBoardingPayload;
  validationErrors?: CommonError[];
  resend?: boolean;
}

export interface InitiateEmployeeOnBoardingFulfilledAction {
  type: TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FULFILLED;
}

export type InitiateEmployeeOnBoardingFailedAction = ErrorAction<TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FAILED>;

export type InitiateEmployeeOnBoardingResendFailedAction =
  ErrorAction<TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED>;

export const initiateEmployeeOnBoarding = (
  payload: EmployeeOnBoardingPayload,
  validationErrors?: CommonError[],
  resend = false
): InitiateEmployeeOnBoardingAction => ({
  id: generateActionId(),
  payload,
  type: TypeKeys.INITIATE_EMPLOYEE_ONBOARDING,
  validationErrors,
  resend,
});

export const initiateEmployeeOnBoardingFulfilled = (): InitiateEmployeeOnBoardingFulfilledAction => ({
  type: TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FULFILLED,
});

export const initiateEmployeeOnBoardingFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): InitiateEmployeeOnBoardingFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_FAILED,
});

export const initiateEmployeeOnBoardingResendFailed = (
  message: string,
  status: number,
  errors?: GenericError[],
  params?: { [s: string]: string }
): InitiateEmployeeOnBoardingResendFailedAction => ({
  errors: convertToCommonErrors(message, status, errors),
  params,
  type: TypeKeys.INITIATE_EMPLOYEE_ONBOARDING_RESEND_FAILED,
});

export interface OmalaskuOrderSubmittedAction {
  type: TypeKeys.OMALASKU_ORDER_SUBMITTED;
}

export const omalaskuOrderSubmitted = (): OmalaskuOrderSubmittedAction => ({
  type: TypeKeys.OMALASKU_ORDER_SUBMITTED,
});

export interface OmalaskuOrderFailedToSubmitAction {
  type: TypeKeys.OMALASKU_ORDER_FAILED_TO_SUBMIT;
}

export const omalaskuOrderFailedToSubmit = (): OmalaskuOrderFailedToSubmitAction => ({
  type: TypeKeys.OMALASKU_ORDER_FAILED_TO_SUBMIT,
});

export type EmployeeActionTypes =
  | ResendEmailVerificationLinkAction
  | ResendEmailVerificationLinkFulfilledAction
  | ResendEmailVerificationLinkFailedAction
  | InitiateEmployeeOnBoardingAction
  | InitiateEmployeeOnBoardingFailedAction
  | InitiateEmployeeOnBoardingResendFailedAction
  | InitiateEmployeeOnBoardingFulfilledAction
  | OmalaskuOrderSubmittedAction
  | OmalaskuOrderFailedToSubmitAction;
